import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, Menu, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetClose, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { residentialServices, commercialServices, serviceAreas } from "@/data/services";
import { BRAND_ASSETS } from "@/lib/brandAssets";

const LOGO_URL = BRAND_ASSETS.logo;

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => { setOpenDropdown(null); }, [location]);

  const handleAnchorClick = (href) => {
    if (href.startsWith("/#")) {
      const id = href.substring(2);
      if (location.pathname === "/") {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      } else {
        window.location.href = href;
      }
    }
  };

  const toggleDropdown = (name) => {
    setOpenDropdown(prev => prev === name ? null : name);
  };

  return (
    <>
      {openDropdown && (
        <div className="fixed inset-0 z-40" onClick={() => setOpenDropdown(null)} />
      )}
      <header
        className="fixed top-0 left-0 right-0 z-50 bg-[#0D0D0D] border-b border-white/10"
        data-testid="header"
      >
        <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-24 sm:h-28">
            {/* Real Company Logo - Large & Prominent */}
            <Link to="/" className="flex shrink-0 items-center" data-testid="header-logo">
              <img
                src={LOGO_URL}
                alt="Junk In Da Truck"
                className="h-14 w-14 min-[375px]:h-20 min-[375px]:w-20 sm:h-24 sm:w-24 shrink-0 object-contain rounded-xl"
              />
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden xl:flex items-center gap-0.5 flex-nowrap min-w-0" data-testid="header-nav">
              <Link to="/" className="px-3 py-2 text-sm font-medium text-gray-300 hover:text-[#97BF04] transition-colors rounded-lg hover:bg-white/5">
                Home
              </Link>
              {/* Services Dropdown */}
              <div className="relative">
                <button
                  onClick={() => toggleDropdown("services")}
                  className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-300 hover:text-[#97BF04] transition-colors rounded-lg hover:bg-white/5"
                >
                  Services <ChevronDown className={`w-3.5 h-3.5 transition-transform ${openDropdown === "services" ? "rotate-180" : ""}`} />
                </button>
                {openDropdown === "services" && (
                  <div className="absolute top-full left-0 mt-2 w-[560px] max-h-[calc(100vh-120px)] bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden flex flex-col">
                    <div className="px-5 pb-4 pt-3 border-b border-gray-100 bg-gradient-to-r from-[#97BF04]/10 via-white to-white shrink-0">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className="text-[10px] font-semibold text-[#2C4001] uppercase tracking-[0.18em] mb-2">Junk In Da Truck Services</p>
                          <h3 className="text-lg font-semibold text-[#0D0D0D] leading-tight">Residential and commercial hauling built for speed</h3>
                          <p className="text-xs text-gray-500 mt-1 max-w-[320px]">Fast pickups, property cleanouts, and no-drama service across the Treasure Coast and Space Coast.</p>
                        </div>
                        <a
                          href="/estimate"
                          onClick={() => setOpenDropdown(null)}
                          className="shrink-0 inline-flex items-center rounded-xl bg-[#0D0D0D] px-3 py-2 text-xs font-semibold text-white hover:bg-[#1a1a1a] transition-colors"
                        >
                          Get Quote
                        </a>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap mt-3 text-[11px]">
                        <span className="inline-flex items-center rounded-full bg-white px-2.5 py-1 font-semibold text-[#2C4001] border border-[#97BF04]/20">Licensed & insured</span>
                        <span className="inline-flex items-center rounded-full bg-white px-2.5 py-1 font-semibold text-[#2C4001] border border-[#97BF04]/20">Same-day service</span>
                        <span className="inline-flex items-center rounded-full bg-white px-2.5 py-1 font-semibold text-[#2C4001] border border-[#97BF04]/20">Upfront pricing</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-0 overflow-y-auto min-h-0">
                      <div className="px-4 py-3">
                        <div className="px-1 py-1.5 mb-2 sticky top-0 bg-white z-10">
                          <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Residential</span>
                        </div>
                        <div className="space-y-1">
                          {residentialServices.map(s => (
                            <Link
                              key={s.slug}
                              to={`/services/${s.slug}`}
                              onClick={() => setOpenDropdown(null)}
                              className="flex items-start gap-3 px-2 py-2.5 hover:bg-gray-50 transition-colors rounded-xl"
                            >
                              <div className="w-9 h-9 bg-[#97BF04]/10 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                                <s.icon className="w-4 h-4 text-[#2C4001]" />
                              </div>
                              <div className="min-w-0">
                                <div className="text-sm font-semibold text-gray-800 leading-tight">{s.title}</div>
                                <p className="text-[11px] text-gray-500 leading-snug mt-1">{s.shortDesc}</p>
                              </div>
                            </Link>
                          ))}
                        </div>
                      </div>
                      <div className="px-4 py-3 border-l border-gray-100 bg-gray-50/40">
                        <div className="px-1 py-1.5 mb-2 sticky top-0 bg-gray-50/95 backdrop-blur-sm z-10">
                          <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Commercial</span>
                        </div>
                        <div className="space-y-1">
                          {commercialServices.map(s => (
                            <Link
                              key={s.slug}
                              to={`/services/${s.slug}`}
                              onClick={() => setOpenDropdown(null)}
                              className="flex items-start gap-3 px-2 py-2.5 hover:bg-white transition-colors rounded-xl"
                            >
                              <div className="w-9 h-9 bg-[#97BF04]/10 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                                <s.icon className="w-4 h-4 text-[#2C4001]" />
                              </div>
                              <div className="min-w-0">
                                <div className="text-sm font-semibold text-gray-800 leading-tight">{s.title}</div>
                                <p className="text-[11px] text-gray-500 leading-snug mt-1">{s.shortDesc}</p>
                              </div>
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="border-t border-gray-100 px-5 pt-4 pb-3 bg-[#0D0D0D] text-white shrink-0">
                      <div className="flex items-center justify-between gap-4">
                        <div>
                          <p className="text-[10px] uppercase tracking-[0.18em] text-white/60 mb-1">Fast-turn cleanup help</p>
                          <p className="text-sm font-semibold leading-tight">Licensed & insured • Same-day service • Get market-ready faster</p>
                        </div>
                        <a
                          href="/estimate"
                          onClick={() => setOpenDropdown(null)}
                          className="shrink-0 inline-flex items-center rounded-xl bg-[#97BF04] px-3 py-2 text-xs font-semibold text-[#0D0D0D] hover:bg-[#83A603] transition-colors"
                        >
                          Get Quote →
                        </a>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Service Areas Dropdown */}
              <div className="relative">
                <button
                  onClick={() => toggleDropdown("areas")}
                  className="inline-flex items-center gap-1 whitespace-nowrap px-2.5 py-2 text-sm font-medium text-gray-300 hover:text-[#97BF04] transition-colors rounded-lg hover:bg-white/5"
                >
                  <span className="whitespace-nowrap">Service Areas</span> <ChevronDown className={`w-3.5 h-3.5 transition-transform ${openDropdown === "areas" ? "rotate-180" : ""}`} />
                </button>
                {openDropdown === "areas" && (
                  <div className="absolute top-full right-0 mt-2 w-[520px] max-h-[calc(100vh-120px)] bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden flex flex-col">
                    <div className="px-5 pb-4 pt-3 border-b border-gray-100 bg-gradient-to-r from-[#97BF04]/10 via-white to-white shrink-0">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className="text-[10px] font-semibold text-[#2C4001] uppercase tracking-[0.18em] mb-2">Service Areas</p>
                          <h3 className="text-lg font-semibold text-[#0D0D0D] leading-tight">Treasure Coast and Space Coast coverage</h3>
                          <p className="text-xs text-gray-500 mt-1 max-w-[320px]">Fast junk removal coverage across the cities we serve most. Pick your city and go straight to the local page.</p>
                        </div>
                        <Link
                          to="/service-areas"
                          onClick={() => setOpenDropdown(null)}
                          className="shrink-0 inline-flex items-center rounded-xl bg-[#0D0D0D] px-3 py-2 text-xs font-semibold text-white hover:bg-[#1a1a1a] transition-colors"
                        >
                          View All
                        </Link>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap mt-3 text-[11px]">
                        <span className="inline-flex items-center rounded-full bg-white px-2.5 py-1 font-semibold text-[#2C4001] border border-[#97BF04]/20">Treasure Coast</span>
                        <span className="inline-flex items-center rounded-full bg-white px-2.5 py-1 font-semibold text-[#2C4001] border border-[#97BF04]/20">Space Coast</span>
                        <span className="inline-flex items-center rounded-full bg-white px-2.5 py-1 font-semibold text-[#2C4001] border border-[#97BF04]/20">Local city pages</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-0 overflow-y-auto min-h-0">
                      <div className="px-4 py-3">
                        <div className="px-1 py-1.5 mb-2 sticky top-0 bg-white z-10">
                          <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Treasure Coast</span>
                        </div>
                        <div className="space-y-1">
                          {serviceAreas.treasureCoast.map(a => (
                            <Link key={a.slug} to={`/service-areas/${a.slug}`} onClick={() => setOpenDropdown(null)}
                              className="flex items-center justify-between gap-3 px-2 py-2.5 text-sm text-gray-700 hover:bg-gray-50 rounded-xl transition-colors">
                              <span className="font-medium">{a.city}</span>
                              <span className="text-[#2C4001] text-xs font-semibold">View</span>
                            </Link>
                          ))}
                        </div>
                      </div>
                      <div className="px-4 py-3 border-l border-gray-100 bg-gray-50/40">
                        <div className="px-1 py-1.5 mb-2 sticky top-0 bg-gray-50/95 backdrop-blur-sm z-10">
                          <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Space Coast</span>
                        </div>
                        <div className="space-y-1">
                          {serviceAreas.spaceCoast.map(a => (
                            <Link key={a.slug} to={`/service-areas/${a.slug}`} onClick={() => setOpenDropdown(null)}
                              className="flex items-center justify-between gap-3 px-2 py-2.5 text-sm text-gray-700 hover:bg-white rounded-xl transition-colors">
                              <span className="font-medium">{a.city}</span>
                              <span className="text-[#2C4001] text-xs font-semibold">View</span>
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="border-t border-gray-100 px-5 pt-4 pb-3 bg-[#0D0D0D] text-white shrink-0">
                      <div className="flex items-center justify-between gap-4">
                        <div>
                          <p className="text-[10px] uppercase tracking-[0.18em] text-white/60 mb-1">Coverage built for fast response</p>
                          <p className="text-sm font-semibold leading-tight">Serving the cities that drive the most calls, cleanouts, and same-day jobs</p>
                        </div>
                        <Link
                          to="/service-areas"
                          onClick={() => setOpenDropdown(null)}
                          className="shrink-0 inline-flex items-center rounded-xl bg-[#97BF04] px-3 py-2 text-xs font-semibold text-[#0D0D0D] hover:bg-[#83A603] transition-colors"
                        >
                          Explore Areas →
                        </Link>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <Link to="/gallery" className="px-2.5 py-2 text-sm font-medium text-gray-300 hover:text-[#97BF04] transition-colors rounded-lg hover:bg-white/5 whitespace-nowrap">
                Gallery
              </Link>
              <Link to="/testimonials" className="px-2.5 py-2 text-sm font-medium text-gray-300 hover:text-[#97BF04] transition-colors rounded-lg hover:bg-white/5 whitespace-nowrap">
                Reviews
              </Link>
              <Link to="/faq" className="px-2.5 py-2 text-sm font-medium text-gray-300 hover:text-[#97BF04] transition-colors rounded-lg hover:bg-white/5 whitespace-nowrap">
                FAQ
              </Link>
              <Link to="/contact" className="px-2.5 py-2 text-sm font-medium text-gray-300 hover:text-[#97BF04] transition-colors rounded-lg hover:bg-white/5 whitespace-nowrap">
                Contact
              </Link>
            </nav>

            {/* CTA + Mobile Menu */}
            <div className="flex shrink-0 items-center gap-2 sm:gap-3">
              <a href="/estimate" className="call-pulse">
                <Button className="bg-[#97BF04] text-[#0D0D0D] hover:bg-[#83A603] font-semibold gap-2 text-sm px-3 sm:px-4">
                  Get Quote
                </Button>
              </a>
              <a href="tel:+17724945048" data-testid="header-call-link" className="call-pulse">
                <Button className="bg-[#97BF04] text-[#0D0D0D] hover:bg-[#83A603] font-semibold gap-2 text-sm px-3 sm:px-4">
                  <Phone className="w-4 h-4" />
                  <span className="hidden sm:inline">(772) 494-5048</span>
                  <span className="sm:hidden">Call</span>
                </Button>
              </a>

              {/* Mobile Menu */}
              <Sheet>
                <SheetTrigger asChild className="xl:hidden">
                  <Button variant="ghost" size="icon" className="text-white hover:bg-white/10" data-testid="header-mobile-menu" aria-label="Open navigation menu">
                    <Menu className="w-5 h-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-full max-w-[320px] sm:max-w-[320px] bg-white overflow-hidden [&>button]:h-10 [&>button]:w-10 [&>button]:top-2 [&>button]:right-2 [&>button]:flex [&>button]:items-center [&>button]:justify-center [&>button]:bg-white [&>button]:opacity-100">
                  <div className="h-full overflow-y-auto overscroll-contain pt-8">
                  <div className="flex items-center gap-3 mb-4">
                    <img src={LOGO_URL} alt="" className="h-12 w-12 shrink-0 object-contain rounded-lg" />
                    <SheetTitle className="text-lg text-[#0D0D0D]">Explore Junk In Da Truck</SheetTitle>
                    <SheetDescription className="sr-only">Main pages, services and the cities we serve.</SheetDescription>
                  </div>
                  <nav aria-label="Mobile navigation" className="space-y-1">
                    {[
                      ["/", "Home"], ["/gallery", "Gallery"], ["/testimonials", "Reviews"],
                      ["/faq", "FAQ"], ["/contact", "Contact"],
                    ].map(([href, label]) => (
                      <SheetClose key={href} asChild>
                        <Link to={href} className="flex min-h-11 items-center rounded-lg px-3 text-sm font-semibold text-gray-800 hover:bg-gray-100">{label}</Link>
                      </SheetClose>
                    ))}
                    <details className="group border-t border-gray-200 mt-3">
                      <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between px-3 text-sm font-semibold text-[#2C4001]">
                        Services <ChevronDown aria-hidden="true" className="h-4 w-4 group-open:rotate-180" />
                      </summary>
                      {[["Residential", residentialServices], ["Commercial", commercialServices]].map(([label, services]) => (
                        <div key={label} className="pb-3">
                          <p className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-600">{label}</p>
                          {services.map(service => (
                            <SheetClose key={service.slug} asChild>
                              <Link to={`/services/${service.slug}`} className="flex min-h-11 items-center gap-2 rounded-lg px-3 text-sm text-gray-700 hover:bg-gray-100">
                                <service.icon aria-hidden="true" className="h-4 w-4 shrink-0 text-[#2C4001]" />{service.title}
                              </Link>
                            </SheetClose>
                          ))}
                        </div>
                      ))}
                    </details>
                    <details className="group border-t border-b border-gray-200">
                      <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between px-3 text-sm font-semibold text-[#2C4001]">
                        Service Areas <ChevronDown aria-hidden="true" className="h-4 w-4 group-open:rotate-180" />
                      </summary>
                      {[["Treasure Coast", serviceAreas.treasureCoast], ["Space Coast", serviceAreas.spaceCoast]].map(([label, areas]) => (
                        <div key={label} className="pb-3">
                          <p className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-600">{label}</p>
                          {areas.map(area => (
                            <SheetClose key={area.slug} asChild>
                              <Link to={`/service-areas/${area.slug}`} className="flex min-h-11 items-center rounded-lg px-3 text-sm text-gray-700 hover:bg-gray-100">{area.city}</Link>
                            </SheetClose>
                          ))}
                        </div>
                      ))}
                      <SheetClose asChild><Link to="/service-areas" className="flex min-h-11 items-center px-3 text-sm font-semibold text-[#2C4001] underline">View all service areas</Link></SheetClose>
                    </details>
                    <div className="pt-4 space-y-3">
                      <SheetClose asChild>
                        <a href="/estimate" className="flex min-h-11 items-center justify-center rounded-lg bg-[#97BF04] px-3 text-sm font-semibold text-[#0D0D0D] hover:bg-[#83A603]">Get Quote</a>
                      </SheetClose>
                      <a href="tel:+17724945048" className="flex min-h-11 items-center justify-center gap-2 rounded-lg bg-[#0D0D0D] px-3 text-sm font-semibold text-white"><Phone aria-hidden="true" className="h-4 w-4" />(772) 494-5048</a>
                    </div>
                  </nav>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;
